/**
 * Area `document-preview` of the console gateway — over `apps/api/src/routes/document-preview/` (design §28.4.2). Owned by
 * plan-015 unit **PRV** (gap G6, owner answer 10, ADR 0049, design §28.5.12): the wizard's menu preview — what WeTalk
 * understood from the menu, shown before the person builds.
 *
 * `previewDocument` stages one file: it hashes the bytes in the browser, asks for a preview, and — unless the API
 * answers a preview of the very same file already (`upload: null`, nothing to send, nothing read again) — PUTs the
 * bytes straight to the write-only link and confirms them. `readDocumentPreview` is polled until the preview is `done`
 * (the counts and the first items with their prices) or `failed` (one plain sentence). Every figure arrives from the
 * API and is carried as it is; the factory counted it.
 *
 * The register is EMPTY: all three operations are served.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readNullableText } from "../conversation-view.js";
import { MalformedResponseError, readArray, readRecord, readText, readWholeNumber } from "../decode.js";
import { contentTypeOfFile, DocumentUploadError, sha256HexOfFile, xhrBlobPut, type BlobPut } from "./agent-document.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

/** `DocumentPreview.state`. */
export type DocumentPreviewStateView = "uploading" | "queued" | "running" | "done" | "failed";

/** One size's price of a sample item. */
export interface DocumentPreviewSizePriceView {
  readonly size: string;
  readonly priceMinor: number;
}

/** One of the first items the menu lists, with its price (its lowest size price when sold in sizes). */
export interface DocumentPreviewSampleView {
  readonly name: string;
  readonly priceMinor: number;
  readonly currency: string;
  readonly sizePrice: readonly DocumentPreviewSizePriceView[];
}

/** One menu preview, as the wizard shows it. */
export interface DocumentPreviewView {
  readonly documentPreviewId: string;
  readonly state: DocumentPreviewStateView;
  readonly filename: string;
  /** How many items the menu offers; `null` until `done`. */
  readonly itemCount: number | null;
  /** How many extras (toppings, crusts) it offers; `null` until `done`. */
  readonly extraCount: number | null;
  /** At most four; empty until `done`. */
  readonly sample: readonly DocumentPreviewSampleView[];
  /** The one plain sentence a failed preview carries; `null` otherwise. */
  readonly failureReason: string | null;
}

/** What a preview is asked for: the template being set up, the menu's language, and the file. */
export interface DocumentPreviewRequestView {
  readonly agentTemplateCode: string;
  readonly languageCode: string;
  readonly file: File;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /**
     * plan-015 PRV: stage a menu for its preview — hash, ask, PUT and confirm, or nothing at all when the same file was
     * read already. Answers the preview as it stands (`queued` while being read).
     */
    previewDocument(request: DocumentPreviewRequestView): Promise<DocumentPreviewView>;
    /** plan-015 PRV: one preview, polled while it is being read. */
    readDocumentPreview(documentPreviewId: string): Promise<DocumentPreviewView>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type DocumentPreviewGatewaySlice = Pick<ConsoleGateway, "previewDocument" | "readDocumentPreview">;

/** This area's rows of `GATEWAY_GAP`: none — every operation is served. */
export const documentPreviewGap: readonly GatewayGap[] = [];

const STATE: readonly DocumentPreviewStateView[] = ["uploading", "queued", "running", "done", "failed"];

function nullableWhole(value: unknown, path: string): number | null {
  return value === null ? null : readWholeNumber(value, path);
}

/** One `DocumentPreview`. */
export function documentPreviewOf(wire: unknown, path: string): DocumentPreviewView {
  const row = readRecord(wire, path);
  const raw = readText(row["state"], `${path}.state`);
  const state = STATE.find((one) => one === raw);
  if (state === undefined) throw new MalformedResponseError(`${path}.state`, "a preview state", raw);
  return {
    documentPreviewId: readText(row["document_preview_id"], `${path}.document_preview_id`),
    state,
    filename: readText(row["filename"], `${path}.filename`),
    itemCount: nullableWhole(row["item_count"], `${path}.item_count`),
    extraCount: nullableWhole(row["extra_count"], `${path}.extra_count`),
    sample: readArray(row["sample"], `${path}.sample`).map((one, index) => {
      const at = `${path}.sample[${index}]`;
      const item = readRecord(one, at);
      return {
        name: readText(item["name"], `${at}.name`),
        priceMinor: readWholeNumber(item["price_minor"], `${at}.price_minor`),
        currency: readText(item["currency"], `${at}.currency`),
        sizePrice: readArray(item["size_price"], `${at}.size_price`).map((each, sized) => {
          const where = `${at}.size_price[${sized}]`;
          const price = readRecord(each, where);
          return { size: readText(price["size"], `${where}.size`), priceMinor: readWholeNumber(price["price_minor"], `${where}.price_minor`) };
        }),
      };
    }),
    failureReason: readNullableText(row["failure_reason"], `${path}.failure_reason`),
  };
}

function headerOf(value: unknown, path: string): Record<string, string> {
  const record = readRecord(value, path);
  const header: Record<string, string> = {};
  for (const [name, one] of Object.entries(record)) header[name] = readText(one, `${path}.${name}`);
  return header;
}

export function documentPreviewApiSlice(client: WeTalkApiClient, _context: SliceContext, put: BlobPut = xhrBlobPut): DocumentPreviewGatewaySlice {
  const dataOf = (body: unknown, operationId: string): Readonly<Record<string, unknown>> =>
    readRecord(readRecord(body, operationId)["data"], `${operationId}.data`);
  return {
    async previewDocument(request) {
      const contentType = contentTypeOfFile(request.file);
      if (contentType === null) throw new DocumentUploadError(`${request.file.name} is not a file type the agent can read.`);
      const sha256 = await sha256HexOfFile(request.file);
      const staged = dataOf(
        assertOk(
          await client.createDocumentPreviewUpload({
            body: {
              agent_template_code: request.agentTemplateCode,
              language_code: request.languageCode,
              filename: request.file.name,
              content_type: contentType,
              byte_size: request.file.size,
              sha256,
            },
          }),
        ),
        "createDocumentPreviewUpload",
      );
      const preview = documentPreviewOf(staged, "createDocumentPreviewUpload.data");
      const upload = staged["upload"];
      // The same file was read already: there is nothing to send.
      if (upload === null) return preview;
      const link = readRecord(upload, "createDocumentPreviewUpload.data.upload");
      await put(
        readText(link["url"], "createDocumentPreviewUpload.data.upload.url"),
        headerOf(link["header"], "createDocumentPreviewUpload.data.upload.header"),
        request.file,
        () => undefined,
      );
      const confirmed = assertOk(await client.confirmDocumentPreview({ path: { document_preview_id: preview.documentPreviewId } }));
      return documentPreviewOf(dataOf(confirmed, "confirmDocumentPreview"), "confirmDocumentPreview.data");
    },
    async readDocumentPreview(documentPreviewId) {
      const body = assertOk(await client.readDocumentPreview({ path: { document_preview_id: documentPreviewId } }));
      return documentPreviewOf(dataOf(body, "readDocumentPreview"), "readDocumentPreview.data");
    },
  };
}
